import React from "react";

import {
  getAiSalesRecommendation,
  getConversionProbability,
  generateFollowUpEmail,
  generateMeetingBrief
} from "../../utils/crm/aiSales";

import { formatDateForInput } from "../../utils/crm/dateHelpers";

const PIPELINE_STAGES = ["New", "Contacted", "Qualified", "Proposal", "Won", "Lost"];

export default function LeadDetailDrawer({
  selectedLead,
  setSelectedLead,
  crmStatus,
  setCrmStatus,
  newTimelineNote,
  setNewTimelineNote,
  emailDraft,
  setEmailDraft,
  meetingBrief,
  setMeetingBrief,
  updateLeadStage,
  updateLeadCrmFields,
  addTimelineNote,
  getBadgeStyle
}) {
  if (!selectedLead) return null;

  return (
    <div style={detailStyle}>
      <button onClick={() => setSelectedLead(null)} style={closeBtn}>
        Close
      </button>

      <h2>Lead Profile</h2>

      <p><b>Name:</b> {selectedLead.name}</p>
      <p><b>Email:</b> {selectedLead.email}</p>
      <p><b>Company:</b> {selectedLead.company}</p>
      <p><b>Industry:</b> {selectedLead.industry || "-"}</p>
      <p><b>Size:</b> {selectedLead.companySize || "-"}</p>

      <hr />

      <p><b>Score:</b> {selectedLead.score}</p>

      <p>
        <b>Status:</b>{" "}
        <span style={getBadgeStyle(selectedLead.temperature)}>
          {selectedLead.temperature}
        </span>
      </p>

      <hr />

      <h4>AI Sales Assistant</h4>

      {(() => {
        const recommendation = getAiSalesRecommendation(selectedLead);
        const probability = getConversionProbability(selectedLead);

        return (
          <div style={aiRecommendationStyle}>
            <p>
              <b>Conversion Probability:</b> {probability}%
            </p>

            <p>
              <b>Priority:</b> {recommendation.priority}
            </p>

            <p>
              <b>Recommended Action:</b> {recommendation.action}
            </p>

            <p>
              <b>Reason:</b> {recommendation.reason}
            </p>
          </div>
        );
      })()}

      <hr />

      <h4>AI Follow-Up Email</h4>

      <button
        onClick={() => setEmailDraft(generateFollowUpEmail(selectedLead))}
        style={saveBtn}
      >
        Generate Follow-Up Email
      </button>

      {emailDraft && (
        <div style={emailDraftStyle}>
          <p>
            <b>Subject:</b> {emailDraft.subject}
          </p>

          <textarea
            rows={10}
            value={emailDraft.body}
            onChange={(event) =>
              setEmailDraft((prev) => ({
                ...prev,
                body: event.target.value
              }))
            }
            style={{ ...inputStyle, resize: "vertical" }}
          />

          <a
            href={`mailto:${selectedLead.email}?subject=${encodeURIComponent(
              emailDraft.subject
            )}&body=${encodeURIComponent(emailDraft.body)}`}
            style={emailLinkStyle}
          >
            Open in Email App
          </a>
        </div>
      )}

      <hr />

      <h4>AI Meeting Brief</h4>

      <button
        onClick={() => setMeetingBrief(generateMeetingBrief(selectedLead))}
        style={saveBtn}
      >
        Generate Meeting Brief
      </button>

      {meetingBrief && (
        <div style={meetingBriefStyle}>
          <p>
            <strong>Meeting Objective</strong>
          </p>

          <p>{meetingBrief.objective}</p>

          <hr />

          <p>
            <strong>Lead Context</strong>
          </p>

          <ul>
            <li>Company: {meetingBrief.context.company}</li>
            <li>Industry: {meetingBrief.context.industry}</li>
            <li>Stage: {meetingBrief.context.stage}</li>
            <li>Lead Score: {meetingBrief.context.score}</li>
            <li>Conversion Probability: {meetingBrief.context.probability}%</li>
            <li>Timeline Notes: {meetingBrief.context.notesCount}</li>
          </ul>

          <hr />

          <p>
            <strong>Key Talking Points</strong>
          </p>

          <ul>
            {meetingBrief.talkingPoints.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>

          <p>
            <strong>Potential Pain Points</strong>
          </p>

          <ul>
            {meetingBrief.potentialPainPoints.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>

          <p>
            <strong>Suggested Questions</strong>
          </p>

          <ul>
            {meetingBrief.suggestedQuestions.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>

          <hr />

          <p>
            <strong>Suggested Next Step</strong>
          </p>

          <p>{meetingBrief.suggestedNextStep}</p>
        </div>
      )}

      <hr />

      <h4>Pipeline Stage</h4>

      <select
        value={selectedLead.stage || "New"}
        onChange={(event) =>
          updateLeadStage(selectedLead._id, event.target.value)
        }
        style={inputStyle}
      >
        {PIPELINE_STAGES.map((stage) => (
          <option key={stage} value={stage}>
            {stage}
          </option>
        ))}
      </select>

      <hr />

      <h4>CRM Follow-Up</h4>

      <label>Owner</label>
      <input
        value={selectedLead.owner || ""}
        onChange={(event) =>
          setSelectedLead((prev) => ({
            ...prev,
            owner: event.target.value
          }))
        }
        style={inputStyle}
      />

      <label>Next Follow-Up</label>
      <input
        type="date"
        value={formatDateForInput(selectedLead.nextFollowUp)}
        onChange={(event) =>
          setSelectedLead((prev) => ({
            ...prev,
            nextFollowUp: event.target.value
          }))
        }
        style={inputStyle}
      />

      <label>Last Contacted</label>
      <input
        type="date"
        value={formatDateForInput(selectedLead.lastContactedAt)}
        onChange={(event) =>
          setSelectedLead((prev) => ({
            ...prev,
            lastContactedAt: event.target.value
          }))
        }
        style={inputStyle}
      />

      <label>Notes</label>
      <textarea
        rows={6}
        value={selectedLead.notes || ""}
        onChange={(event) =>
          setSelectedLead((prev) => ({
            ...prev,
            notes: event.target.value
          }))
        }
        style={{ ...inputStyle, resize: "vertical" }}
      />

      <button onClick={updateLeadCrmFields} style={saveBtn}>
        Save CRM Updates
      </button>

      <hr />

      <h4>Notes Timeline</h4>

      <textarea
        rows={4}
        placeholder="Add a new timeline note..."
        value={newTimelineNote}
        onChange={(event) => setNewTimelineNote(event.target.value)}
        style={{ ...inputStyle, resize: "vertical" }}
      />

      <button onClick={addTimelineNote} style={saveBtn}>
        Add Timeline Note
      </button>

      <div style={{ marginTop: "16px" }}>
        {Array.isArray(selectedLead.notesTimeline) &&
        selectedLead.notesTimeline.length > 0 ? (
          [...selectedLead.notesTimeline]
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .map((item, index) => (
              <div key={index} style={timelineItemStyle}>
                <p style={{ marginTop: 0 }}>
                  <strong>{new Date(item.createdAt).toLocaleDateString()}</strong>
                </p>

                {item.createdBy && (
                  <p style={{ color: "#64748b" }}>By: {item.createdBy}</p>
                )}

                <p>{item.note}</p>
              </div>
            ))
        ) : (
          <p>No timeline notes yet.</p>
        )}
      </div>

      {crmStatus && <p>{crmStatus}</p>}

      <hr />

      <p><b>Interest:</b> {selectedLead.interest}</p>
      <p><b>Source:</b> {selectedLead.source}</p>

      <p><b>Original Message:</b></p>
      <p>{selectedLead.message || "-"}</p>
    </div>
  );
}

const detailStyle = {
  position: "fixed",
  top: 0,
  right: 0,
  width: 420,
  height: "100%",
  background: "#fff",
  borderLeft: "1px solid #ddd",
  padding: 20,
  overflowY: "auto",
  zIndex: 1000,
  boxShadow: "-4px 0 16px rgba(15, 23, 42, 0.15)"
};

const closeBtn = {
  padding: "6px 10px",
  background: "#ef4444",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  marginBottom: 10
};

const inputStyle = {
  width: "100%",
  padding: "10px",
  marginTop: "6px",
  marginBottom: "12px",
  borderRadius: "8px",
  border: "1px solid #cbd5e1",
  boxSizing: "border-box"
};

const saveBtn = {
  width: "100%",
  padding: "10px",
  borderRadius: "8px",
  border: "none",
  background: "#0f766e",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
  marginTop: "6px"
};

const aiRecommendationStyle = {
  padding: "14px",
  borderRadius: "12px",
  background: "#ecfeff",
  border: "1px solid #a5f3fc",
  marginBottom: "14px"
};

const emailDraftStyle = {
  marginTop: "12px",
  padding: "12px",
  borderRadius: "10px",
  background: "#f8fafc",
  border: "1px solid #e5e7eb"
};

const emailLinkStyle = {
  display: "inline-block",
  marginTop: "8px",
  padding: "10px 12px",
  borderRadius: "8px",
  background: "#0f766e",
  color: "#fff",
  textDecoration: "none",
  fontWeight: 700
};

const meetingBriefStyle = {
  marginTop: "12px",
  padding: "16px",
  borderRadius: "12px",
  background: "#f8fafc",
  border: "1px solid #cbd5e1"
};

const timelineItemStyle = {
  padding: "12px",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  background: "#f8fafc",
  marginBottom: "10px"
};